import { Router } from '@angular/router';
import {
  MsalInterceptorConfiguration,
  MsalGuardConfiguration
} from '@azure/msal-angular';
import {
  LogLevel,
  Configuration,
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication
} from '@azure/msal-browser';

import { MsalNavigationClient } from '@auth/auth.navigation-client';
import { ENVIRONMENT } from '@env/environment';

const CONFIG = ENVIRONMENT.azureAdB2C;

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

export const MSAL_CONFIG: Configuration = {
  auth: {
    clientId: CONFIG.clientId,
    authority: CONFIG.b2cPolicies.authorities.signUpSignIn.authority,
    knownAuthorities: [CONFIG.b2cPolicies.authorityDomain],
    redirectUri: '/',
    postLogoutRedirectUri: '/',
    navigateToLoginRequestUrl: true
  },
  cache: {
    cacheLocation: BrowserCacheLocation.SessionStorage
  },
  system: {
    loggerOptions: {
      loggerCallback(logLevel: LogLevel, message: string): void {
        switch (logLevel) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false
    }
  }
};

export function MSALInstanceFactory(router: Router): IPublicClientApplication {
  const instance = new PublicClientApplication(MSAL_CONFIG);
  instance.setNavigationClient(new MsalNavigationClient(router));
  return instance;
}

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();

  protectedResourceMap.set(
    ENVIRONMENT.coreApi.endpoint,
    ENVIRONMENT.coreApi.scopes
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    authRequest: {
      authority: CONFIG.b2cPolicies.authorities.signUpSignIn.authority
    }
  };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ENVIRONMENT.coreApi.scopes,
      authority: CONFIG.b2cPolicies.authorities.signUpSignIn.authority
    },
    loginFailedRoute: '/error?errorType=login-error'
  };
}
